/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Skeleton, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import { StarFilledIcon } from '@coursera/cds-icons';

import type { PartnerPageStat } from 'bundles/partners-graphql/components/cds/partnerPageStatsUtils';

const LOADING_STAT_COUNT = 4;
const STAT_ITEM_MIN_WIDTH = 140;
const STAT_ITEM_MIN_WIDTH_MOBILE = 112;

const ellipsisText = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;
`;

const styles = {
  row: css`
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    row-gap: var(--cds-spacing-200);
    min-width: 0;
    max-width: 100%;
    padding: var(--cds-spacing-50) 0 var(--cds-spacing-200);
  `,
  scrollItem: css`
    display: flex;
    flex: 0 0 auto;
    min-width: ${STAT_ITEM_MIN_WIDTH}px;
    padding-right: var(--cds-spacing-200);
    margin-right: var(--cds-spacing-200);
    border-right: 1px solid var(--cds-color-neutral-stroke-primary-weak);

    ${breakpoints.down('sm')} {
      min-width: ${STAT_ITEM_MIN_WIDTH_MOBILE}px;
      max-width: ${STAT_ITEM_MIN_WIDTH_MOBILE}px;
      padding-right: var(--cds-spacing-150);
      margin-right: var(--cds-spacing-150);
    }

    &:last-child {
      border-right: none;
      margin-right: 0;
      padding-right: 0;
    }
  `,
  statItem: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
    min-height: 60px;
    height: 100%;
    min-width: 0;
    width: 100%;

    ${breakpoints.down('sm')} {
      min-height: 44px;
      gap: calc(var(--cds-spacing-50) / 2);
    }
  `,
  statTitleSlot: css`
    min-height: 24px;
    display: flex;
    align-items: center;
    min-width: 0;
    width: 100%;

    ${breakpoints.down('sm')} {
      min-height: 18px;
    }
  `,
  statBodySlot: css`
    min-height: 28px;
    display: flex;
    align-items: center;

    ${breakpoints.down('sm')} {
      min-height: 22px;
    }
  `,
  statTitle: css`
    color: var(--cds-color-neutral-primary-weak);
    ${typography2.subtitleMedium};
    ${ellipsisText}

    ${breakpoints.down('sm')} {
      ${typography2.bodySecondary};
    }
  `,
  statBody: css`
    color: var(--cds-color-neutral-primary);
    ${typography2.titleSmall};

    ${breakpoints.down('sm')} {
      ${typography2.subtitleMedium};
    }
  `,
  ratingBody: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-50);
  `,
  ratingStar: css`
    display: flex;

    ${breakpoints.down('sm')} svg {
      width: 16px;
      height: 16px;
    }
  `,
  titleSkeleton: css`
    width: 120px;
    height: 20px;

    ${breakpoints.down('sm')} {
      width: ${STAT_ITEM_MIN_WIDTH_MOBILE}px;
      height: 14px;
    }
  `,
  bodySkeleton: css`
    width: 80px;
    height: 24px;

    ${breakpoints.down('sm')} {
      width: 64px;
      height: 22px;
    }
  `,
};

type Props = {
  stats: PartnerPageStat[];
  loading?: boolean;
};

const PartnerStatSkeleton = () => (
  <div css={styles.scrollItem} aria-hidden="true">
    <div css={styles.statItem}>
      <div css={styles.statTitleSlot}>
        <Skeleton variant="rect" css={styles.titleSkeleton} />
      </div>
      <div css={styles.statBodySlot}>
        <Skeleton variant="rect" css={styles.bodySkeleton} />
      </div>
    </div>
  </div>
);

const PartnerStatItem = ({ stat }: { stat: PartnerPageStat }) => (
  <div css={styles.scrollItem} data-testid={`partner-stat-${stat.id}`} role="listitem">
    <div css={styles.statItem}>
      <div css={styles.statTitleSlot}>
        <Typography2 component="div" css={styles.statTitle} title={stat.title}>
          {stat.title}
        </Typography2>
      </div>
      <div css={styles.statBodySlot}>
        {stat.type === 'rating' ? (
          <div css={styles.ratingBody}>
            <Typography2 component="span" css={styles.statBody}>
              {stat.value}
            </Typography2>
            <span css={styles.ratingStar}>
              <StarFilledIcon size="medium" color="default" aria-hidden="true" />
            </span>
          </div>
        ) : (
          <Typography2 component="div" css={styles.statBody}>
            {stat.value}
          </Typography2>
        )}
      </div>
    </div>
  </div>
);

const PartnerStatsRow = ({ stats, loading = false }: Props) => {
  if (loading) {
    return (
      <div css={styles.row} data-testid="partner-stats-row-loading">
        {Array.from({ length: LOADING_STAT_COUNT }, (_, index) => (
          <PartnerStatSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (stats.length === 0) {
    return null;
  }

  return (
    <div css={styles.row} data-testid="partner-stats-row" role="list">
      {stats.map((stat) => (
        <PartnerStatItem key={stat.id} stat={stat} />
      ))}
    </div>
  );
};

export default PartnerStatsRow;
